import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { DatePicker, Select } from 'antd'
import moment from 'moment'
import dayjs from 'dayjs'
import { toast } from 'react-hot-toast'
import {
  FiUsers, FiStar, FiHeart, FiTrendingUp, FiTrendingDown,
  FiBookOpen, FiShield, FiMic, FiDownload, FiEdit2, FiTrash2, FiCheck, FiX
} from 'react-icons/fi'
import { HiOutlineLightBulb } from 'react-icons/hi2'
import { FaCrown, FaRupeeSign } from 'react-icons/fa'
import {
  MyTeacherPerformanceService, PublicTeacherListService, DownloadTeacherPerformanceReportService,
  ListIncentiveService, AddIncentiveService, UpdateIncentiveService, DeleteIncentiveService
} from '../../../services/TeacherFeedbackServices'
import { BRAND_NAVY, BRAND_GOLD, BG_SOFT } from './PublicHeader'

// Same performance sections as TeacherFeedbackLeaderboard.js (stat cards,
// incentive ledger, detailed performance, recent feedback, teacher of the
// month) but WITHOUT the ranked "Monthly Teacher Leaderboard" list — meant
// to be embedded as a tab/section inside another page (e.g. the main
// dashboard's "Performance" switch) rather than used as its own route.
const CURRENT_MONTH = moment().format('YYYY-MM')

const CATEGORY_META = {
  student_improvement: { icon: FiTrendingUp, color: '#2e9e63' },
  teaching_effectiveness: { icon: FiBookOpen, color: '#3b6fd9' },
  classroom_engagement: { icon: FiUsers, color: '#8347c9' },
  discipline_accountability: { icon: FiShield, color: '#2e9e63' },
  overall_satisfaction: { icon: FiHeart, color: '#d1497a' }
}

function sentiment(rating) {
  if (rating >= 4.5) return { label: 'Excellent', bg: '#eafaf0', text: '#1a9d5c' }
  if (rating >= 4) return { label: 'Very Good', bg: '#e8f0fe', text: '#3b6fd9' }
  if (rating >= 3) return { label: 'Good', bg: '#fdf1de', text: '#d1861f' }
  return { label: 'Needs Attention', bg: '#fdeaec', text: '#d1497a' }
}

export default function TeacherPerformancePanel() {
  const auth = useSelector(state => state.Auth)
  const [performance, setPerformance] = useState(null)
  const [loading, setLoading] = useState(true)

  const [month, setMonth] = useState(CURRENT_MONTH)
  // antd v5's DatePicker (like every other DatePicker in this app) works on
  // dayjs instances, not moment — passing it a moment object silently breaks
  // month selection/highlighting, so this stays dayjs even though the rest
  // of the file uses moment for display formatting.
  const [monthValue, setMonthValue] = useState(dayjs(CURRENT_MONTH, 'YYYY-MM'))
  const [teacherOptions, setTeacherOptions] = useState([])
  const [selectedTeacherId, setSelectedTeacherId] = useState('') // admin-only: which teacher's card to view
  const [downloading, setDownloading] = useState(false)

  const [incentiveEntries, setIncentiveEntries] = useState([])
  const [newIncentiveAmount, setNewIncentiveAmount] = useState('')
  const [newIncentiveRemarks, setNewIncentiveRemarks] = useState('')
  const [savingIncentive, setSavingIncentive] = useState(false)
  const [editingIncentiveId, setEditingIncentiveId] = useState('')
  const [editAmount, setEditAmount] = useState('')
  const [editRemarks, setEditRemarks] = useState('')

  const isAdmin = auth?.roles?.includes('admin')

  useEffect(() => {
    PublicTeacherListService().then(res => {
      if (res?.data?.status) setTeacherOptions((res.data.data || []).map(t => ({ label: t.name, value: t._id })))
    })
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, selectedTeacherId])

  useEffect(() => {
    if (isAdmin && selectedTeacherId) {
      fetchIncentive()
    } else {
      setIncentiveEntries([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedTeacherId, month])

  async function fetchData() {
    setLoading(true)
    const perf = await MyTeacherPerformanceService(month, selectedTeacherId)
    if (perf?.data?.status) setPerformance(perf.data.data)
    setLoading(false)
  }

  async function fetchIncentive() {
    const res = await ListIncentiveService(selectedTeacherId, month)
    if (res?.data?.status) setIncentiveEntries(res.data.entries || [])
  }

  async function handleAddIncentive() {
    const amt = Number(newIncentiveAmount)
    if (!Number.isFinite(amt) || amt === 0) {
      toast.error('Enter a non-zero amount (positive to add, negative to deduct)')
      return
    }
    setSavingIncentive(true)
    const res = await AddIncentiveService(selectedTeacherId, month, amt, newIncentiveRemarks)
    setSavingIncentive(false)
    if (res?.data?.status) {
      toast.success('Incentive adjustment added')
      setNewIncentiveAmount('')
      setNewIncentiveRemarks('')
      fetchIncentive()
      fetchData()
    } else {
      toast.error(res?.data?.message || 'Could not add adjustment')
    }
  }

  async function handleUpdateIncentive(id) {
    const amt = Number(editAmount)
    if (!Number.isFinite(amt) || amt === 0) {
      toast.error('Enter a non-zero amount')
      return
    }
    const res = await UpdateIncentiveService(id, { amount: amt, remarks: editRemarks })
    if (res?.data?.status) {
      toast.success('Adjustment updated')
      setEditingIncentiveId('')
      fetchIncentive()
      fetchData()
    } else {
      toast.error(res?.data?.message || 'Could not update adjustment')
    }
  }

  async function handleDeleteIncentive(id) {
    const res = await DeleteIncentiveService(id)
    if (res?.data?.status) {
      toast.success('Adjustment removed')
      fetchIncentive()
      fetchData()
    } else {
      toast.error(res?.data?.message || 'Could not remove adjustment')
    }
  }

  async function handleDownload() {
    setDownloading(true)
    const res = await DownloadTeacherPerformanceReportService(month, selectedTeacherId)
    setDownloading(false)
    if (res?.data?.status) {
      window.open(res.data.url, '_blank')
    } else {
      toast.error(res?.data?.message || 'Could not generate the report')
    }
  }

  if (loading && !performance) return <div className="p-10 text-center text-gray-400">Loading…</div>

  const satisfactionPercent = performance ? Math.round((performance.average_rating / 5) * 100) : 0
  const satisfactionLabel = satisfactionPercent >= 90 ? 'Highly Rated' : satisfactionPercent >= 75 ? 'Well Rated' : 'Building Trust'

  const teacherName = performance?.teacher?.name || auth?.name || 'Teacher'
  const teacherTag = performance?.teacher?.expertise || 'Teacher'
  const isTeacherAccount = !!performance?.teacher
  const monthLabel = moment(month, 'YYYY-MM').format('MMMM YYYY')

  return (
    <div>
      {/* Controls — month, admin teacher picker, download */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <DatePicker
          size="small"
          picker="month"
          format="MMMM YYYY"
          allowClear={false}
          value={monthValue}
          placeholder='Month Select'
          className="border-gray-200 h-[30px]"
          onChange={(v) => { setMonth(v ? v.format('YYYY-MM') : CURRENT_MONTH); setMonthValue(v) }}
        />

        {isAdmin && (
          <Select
            showSearch
            size="small"
            getPopupContainer={() => document.body}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            value={selectedTeacherId || null}
            allowClear
            onClear={() => setSelectedTeacherId('')}
            style={{ minWidth: 220, height: 30 }}
            options={teacherOptions}
            placeholder="Select a Teacher"
            onChange={(e) => setSelectedTeacherId(e || '')}
          />
        )}

        {/* Download Report — admin-only for now; a teacher viewing their own
            performance no longer gets this button. Admin can still change
            the month above (which now defaults to the current month) to
            pull an older report. */}
        {/* {isAdmin && (
          <button
            onClick={handleDownload}
            disabled={!isTeacherAccount || downloading}
            className="flex items-center gap-1.5 text-[12px] font-bold px-3.5 py-1.5 rounded-md text-white disabled:opacity-40 ml-auto"
            style={{ background: BRAND_NAVY }}
          >
            <FiDownload size={13} /> {downloading ? 'Preparing…' : 'Download Report'}
          </button>
        )} */}
      </div>

      {/* Greeting */}
      <div className="rounded-2xl px-5 py-4 mb-4 flex items-center justify-between flex-wrap gap-3" style={{ background: 'linear-gradient(120deg, #eef2ff 0%, #f7f8fb 100%)' }}>
        <div>
          <p className="font-bold text-gray-900 text-[15px]">Hello, {teacherName} 👋</p>
          <p className="text-gray-500 text-[11px] mt-0.5">Great teachers create great futures!</p>
        </div>
        <span className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-bold" style={{ background: BRAND_NAVY, color: 'white' }}>
          <FiBookOpen size={13} /> {isTeacherAccount ? teacherTag : 'Admin View'}
        </span>
      </div>

      {!isTeacherAccount && (
        <div className="rounded-md px-5 py-3.5 mb-5 flex items-center gap-3 border" style={{ background: '#e8f0fe', borderColor: '#c9dbfc' }}>
          <FiUsers style={{ color: '#3b6fd9' }} size={18} className="flex-shrink-0" />
          <p className="text-[12.5px] text-gray-600 leading-snug">
            You're signed in as an <span className="font-bold" style={{ color: BRAND_NAVY }}>Admin</span>, not a teacher account, so there's no personal rating to show here.
            {isAdmin
              ? ' Use the "Select a Teacher" dropdown above to view — and download a report for — any teacher\'s performance.'
              : ''}
          </p>
        </div>
      )}

      {/* Stat cards */}
      {isTeacherAccount && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatCard
            icon={FiUsers} accent="#3b6fd9"
            label="Parent Feedback"
            value={performance?.total_responses ?? 0}
            caption="responses this month"
            trend={performance?.previous_month?.feedback_change_percent}
          />
          <StatCard
            icon={FiStar} accent={BRAND_GOLD}
            label="Overall Rating"
            value={`${performance?.average_rating ?? 0} / 5`}
            caption="average across all questions"
          />
          <StatCard
            icon={FaCrown} accent="#d1861f"
            label="My Rank"
            value={performance?.rank ? `#${performance.rank}` : '—'}
            caption={performance?.total_teachers ? `out of ${performance.total_teachers} teachers` : 'no ranking yet'}
            rankChange={performance?.previous_month?.rank_change}
          />
          <StatCard
            icon={FiHeart} accent="#d1497a"
            label="Satisfaction"
            value={`${satisfactionPercent}%`}
            sub={satisfactionLabel}
            caption="of parents are happy"
          />
          <StatCard
            icon={FaRupeeSign} accent={(performance?.incentive_amount ?? 0) >= 0 ? '#1a9d5c' : '#d1497a'}
            label="Incentive"
            value={`${(performance?.incentive_amount ?? 0) >= 0 ? '+' : ''}₹${performance?.incentive_amount ?? 0}`}
            caption={monthLabel}
          />
        </div>
      )}

      {/* Incentive adjustments — admin only, once a teacher is selected */}
      {isAdmin && isTeacherAccount && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fdf1de' }}>
              <FaRupeeSign style={{ color: '#d1861f' }} size={13} />
            </span>
            <div>
              <p className="font-bold text-gray-800 text-[14px]">Incentive Adjustments — {teacherName}</p>
              <p className="text-gray-400 text-[11px]">{monthLabel} · Add a bonus (+) or deduction (−) based on performance</p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div>
              <label className="block text-[10.5px] font-bold text-gray-400 uppercase mb-1">Amount (+ / -)</label>
              <input
                type="number"
                value={newIncentiveAmount}
                onChange={e => setNewIncentiveAmount(e.target.value)}
                placeholder="e.g. 1000 or -200"
                className="border border-gray-200 rounded-md px-2.5 py-1.5 text-[12.5px] w-32 focus:outline-none"
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[10.5px] font-bold text-gray-400 uppercase mb-1">Remarks</label>
              <input
                type="text"
                value={newIncentiveRemarks}
                onChange={e => setNewIncentiveRemarks(e.target.value)}
                placeholder="Reason for this adjustment"
                className="border border-gray-200 rounded-md px-2.5 py-1.5 text-[12.5px] w-full focus:outline-none"
              />
            </div>
            <button
              onClick={handleAddIncentive}
              disabled={savingIncentive || !newIncentiveAmount}
              className="text-[12px] font-bold px-3.5 py-1.5 rounded-md text-white disabled:opacity-40"
              style={{ background: BRAND_NAVY }}
            >
              {savingIncentive ? 'Adding…' : 'Add'}
            </button>
          </div>

          <div className="flex items-center gap-2 mb-3 text-[13px]">
            <span className="text-gray-500">Current total for {monthLabel}:</span>
            <span className="font-extrabold" style={{ color: (performance?.incentive_amount ?? 0) >= 0 ? '#1a9d5c' : '#d1497a' }}>
              {(performance?.incentive_amount ?? 0) >= 0 ? '+' : ''}&#8377;{performance?.incentive_amount ?? 0}
            </span>
          </div>

          {incentiveEntries.length === 0 ? (
            <p className="text-[12px] text-gray-400 text-center py-4">No adjustments yet this month.</p>
          ) : (
            <div className="space-y-2">
              {incentiveEntries.map(entry => (
                <IncentiveRow
                  key={entry._id}
                  entry={entry}
                  editing={editingIncentiveId === entry._id}
                  editAmount={editAmount}
                  editRemarks={editRemarks}
                  setEditAmount={setEditAmount}
                  setEditRemarks={setEditRemarks}
                  onEditStart={() => { setEditingIncentiveId(entry._id); setEditAmount(String(entry.amount)); setEditRemarks(entry.remarks || '') }}
                  onEditCancel={() => setEditingIncentiveId('')}
                  onSave={() => handleUpdateIncentive(entry._id)}
                  onDelete={() => handleDeleteIncentive(entry._id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {performance && isTeacherAccount && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {/* Detailed performance */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#e8f0fe' }}>
                <FiTrendingUp style={{ color: '#3b6fd9' }} size={15} />
              </span>
              <p className="font-bold text-gray-800 text-[14px]">My Detailed Performance</p>
            </div>

            <div className="flex items-center gap-5 mb-5">
              <div className="relative flex-shrink-0" style={{ width: 76, height: 76 }}>
                <ProgressRing value={performance.average_rating} max={5} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[15px] font-extrabold text-gray-900 leading-none">{performance.average_rating}</span>
                  <div className="flex gap-[1px] mt-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <FiStar key={n} size={7} fill={n <= Math.round(performance.average_rating) ? BRAND_GOLD : 'none'} style={{ color: BRAND_GOLD }} />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Overall Rating</p>
                <p className="text-2xl font-extrabold text-gray-900">{performance.average_rating} <span className="text-sm font-medium text-gray-400">/ 5</span></p>
                <span className="inline-block text-[10.5px] font-bold px-2 py-0.5 rounded-full mt-1" style={{ background: '#eafaf0', color: '#1a9d5c' }}>
                  {satisfactionLabel}
                </span>
                <p className="text-[10.5px] text-gray-400 mt-1.5 leading-snug max-w-[140px]">Based on all parent ratings this month</p>
              </div>
            </div>

            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-8">Parent Feedback Categories</p>
            <div className="space-y-3">
              {performance.category_breakdown.map((cat, i) => {
                const meta = CATEGORY_META[cat.key] || {}
                const Icon = meta.icon || FiStar
                return (
                  <div key={cat.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] text-gray-600 flex items-center gap-1.5">
                        <Icon size={12} style={{ color: meta.color }} /> <span className="text-gray-400 font-semibold">{i + 1}.</span> {cat.label}
                      </span>
                      <span className="text-[12px] font-bold text-gray-700 flex items-center gap-1">
                        <FiStar size={10} fill={BRAND_GOLD} style={{ color: BRAND_GOLD }} /> {cat.average}
                      </span>
                    </div>
                    <div className="w-full h-[12px] mt-2 opacity-80 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(cat.average / 5) * 100}%`, background: meta.color || BRAND_GOLD }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Recent feedback */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="font-bold text-gray-800 text-[14px] mb-3.5">Recent Parent Feedback</p>
            {performance.recent_feedback.length === 0 ? (
              <p className="text-[12.5px] text-gray-400 text-center py-6">No feedback yet this month.</p>
            ) : (
              <div className="space-y-3">
                {performance.recent_feedback.slice(0, 4).map(f => {
                  const s = sentiment(f.average_rating)
                  return (
                    <div key={f._id} className="rounded-xl p-3" style={{ background: BG_SOFT }}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: BRAND_NAVY }}>
                            {f.student_name?.[0] || 'P'}
                          </span>
                          <span className="text-[11.5px] font-semibold text-gray-700 truncate">
                            Parent of {f.student_name}{f.student_class ? ` (${f.student_class})` : ''}
                          </span>
                        </div>
                        <span className="text-[9.5px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: s.bg, color: s.text }}>{s.label}</span>
                      </div>
                      <div className="flex items-center gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map(n => (
                          <FiStar key={n} size={11} fill={n <= Math.round(f.average_rating) ? BRAND_GOLD : 'none'} style={{ color: BRAND_GOLD }} />
                        ))}
                        <span className="text-[10.5px] font-bold text-gray-500 ml-1">{f.average_rating}</span>
                      </div>
                      {f.feedback_text && <p className="text-[12px] text-gray-600 italic leading-snug">"{f.feedback_text}"</p>}
                      {f.voice_feedback_url && (
                        <p className="text-[11px] font-semibold flex items-center gap-1 mt-1" style={{ color: BRAND_NAVY }}>
                          <FiMic size={11} /> Voice feedback attached
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Teacher of the month */}
      {performance?.teacher_of_the_month && (
        <div className="rounded-md p-5 mb-5 border relative overflow-hidden" style={{ background: 'linear-gradient(120deg, #fdf1de 0%, #fdeaec 100%)', borderColor: '#f5e0bd' }}>
          {CONFETTI.map((c, i) => (
            <span key={i} className="absolute w-2 h-2 rounded-full opacity-70" style={{ top: c.top, left: c.left, right: c.right, background: c.color }} />
          ))}
          <div className="flex items-center gap-2 mb-3">
            <FaCrown style={{ color: '#d1861f' }} size={16} />
            <p className="font-bold text-gray-800 text-[14px]">Teacher of the Month — {monthLabel}</p>
          </div>
          <div className="flex items-center gap-3.5">
            <div className="w-16 h-16 rounded-full bg-white overflow-hidden flex items-center justify-center text-lg font-bold text-gray-400 flex-shrink-0 shadow-md" style={{ boxShadow: '0 0 0 3px white, 0 0 0 5px #f5e0bd' }}>
              {performance.teacher_of_the_month.profile_photo
                ? <img src={`${process.env.REACT_APP_AWS_IMAGE_URL || ''}${performance.teacher_of_the_month.profile_photo}`} alt="" className="w-full h-full object-cover" />
                : performance.teacher_of_the_month.name?.[0]}
            </div>
            <div>
              <p className="font-medium text-gray-900 text-[14px]">{performance.teacher_of_the_month.name}</p>
              {performance.teacher_of_the_month.expertise && <p className="text-gray-500 text-[10px]">{performance.teacher_of_the_month.expertise}</p>}
              <p className="text-[10px] font-bold flex items-center gap-1 mt-0.5" style={{ color: '#d1861f' }}>
                <FiStar size={12} fill="#d1861f" /> {performance.teacher_of_the_month.average_rating} / 5 · {performance.teacher_of_the_month.total_responses} reviews
              </p>
              <p className="text-[10px] text-gray-500 mt-1">
                {performance.is_teacher_of_the_month
                  ? "That's you! Highest average rating from parents this month. 🎉"
                  : 'Highest average rating from parents this month.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Motivational banner */}
      <div className="rounded-md px-5 py-4 flex items-center gap-3" style={{ background: BRAND_NAVY }}>
        <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,166,35,0.2)' }}>
          <HiOutlineLightBulb style={{ color: BRAND_GOLD }} size={18} />
        </span>
        <div>
          <p className="text-white font-bold text-[13px]">Your dedication creates better learning experiences!</p>
          <p className="text-white/60 text-[11.5px] mt-0.5">Keep inspiring students and building brighter futures together.</p>
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon: Icon, accent, label, value, sub, caption, trend, rankChange }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1a` }}>
          <Icon style={{ color: accent }} size={15} />
        </span>
        {typeof trend === 'number' && (
          <span className={`text-[10px] font-bold flex items-center gap-0.5 ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <FiTrendingUp size={10} /> : <FiTrendingDown size={10} />} {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
        {typeof rankChange === 'number' && rankChange !== 0 && (
          <span className={`text-[10px] font-bold flex items-center gap-0.5 ${rankChange > 0 ? 'text-green-600' : 'text-red-500'}`}>
            {rankChange > 0 ? <FiTrendingUp size={10} /> : <FiTrendingDown size={10} />} {Math.abs(rankChange)}
          </span>
        )}
      </div>
      <p className="text-[17px] font-extrabold text-gray-900 leading-tight">{value}</p>
      <p className="text-[10.5px] text-gray-400 mt-0.5 leading-tight">{label}</p>
      {sub && <p className="text-[10px] font-semibold mt-0.5" style={{ color: accent }}>{sub}</p>}
      {caption && <p className="text-[9.5px] text-gray-300 mt-0.5 leading-tight">{caption}</p>}
    </div>
  )
}

function IncentiveRow({ entry, editing, editAmount, editRemarks, setEditAmount, setEditRemarks, onEditStart, onEditCancel, onSave, onDelete }) {
  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2.5">
        <input
          type="number"
          value={editAmount}
          onChange={e => setEditAmount(e.target.value)}
          className="border border-gray-200 rounded-md px-2 py-1 text-[12px] w-24 focus:outline-none"
          autoFocus
        />
        <input
          type="text"
          value={editRemarks}
          onChange={e => setEditRemarks(e.target.value)}
          placeholder="Remarks"
          className="border border-gray-200 rounded-md px-2 py-1 text-[12px] flex-1 min-w-[120px] focus:outline-none"
        />
        <button onClick={onSave} className="text-green-600 hover:text-green-700 flex-shrink-0" title="Save"><FiCheck size={16} /></button>
        <button onClick={onEditCancel} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Cancel"><FiX size={16} /></button>
      </div>
    )
  }
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg p-2.5" style={{ background: BG_SOFT }}>
      <div className="min-w-0">
        <span className="font-bold text-[13px]" style={{ color: entry.amount >= 0 ? '#1a9d5c' : '#d1497a' }}>
          {entry.amount >= 0 ? '+' : ''}&#8377;{entry.amount}
        </span>
        {entry.remarks && <p className="text-[11px] text-gray-500 truncate">{entry.remarks}</p>}
        <p className="text-[10px] text-gray-400 mt-0.5">
          {entry.added_by?.name ? `by ${entry.added_by.name} · ` : ''}{moment(entry.createdAt).format('D MMM, h:mm A')}
        </p>
      </div>
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <button onClick={onEditStart} className="text-gray-400 hover:text-gray-700" title="Edit"><FiEdit2 size={13} /></button>
        <button onClick={onDelete} className="text-gray-400 hover:text-red-500" title="Delete"><FiTrash2 size={13} /></button>
      </div>
    </div>
  )
}

const CONFETTI = [
  { top: '-6%', left: '8%', color: '#f5a623' },
  { top: '2%', right: '4%', color: '#3b6fd9' },
  { top: '20%', left: '-4%', color: '#2e9e63' },
  { top: '15%', right: '-6%', color: '#d1497a' }
]

function ProgressRing({ value, max, size = 76 }) {
  const stroke = 7
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const pct = Math.min(value / max, 1)
  const offset = circumference * (1 - pct)
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={BG_SOFT} strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="#2e9e63" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  )
}
